import Papa from 'papaparse';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { alpha } from '@mui/material/styles';
// @mui
import {
  Box,
  Link,
  Chip,
  Stack,
  Toolbar,
  Tooltip,
  Popover,
  MenuItem,
  IconButton,
  Typography,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import PermissionGuard from 'src/guards/PermissionGuard';

// components
import Iconify from '../iconify';
import { useConfirmModal } from '../../hooks/useModal';
import { toolbarStyle, StyledSearch, boxSelectedStyle } from './style';

TableToolbar.propTypes = {
  numSelected: PropTypes.number,
  filterName: PropTypes.string,
  setFilterName: PropTypes.func,
  onFilterName: PropTypes.func,
  filterBy: PropTypes.string,
  model: PropTypes.string,
  handleOpenFilter: PropTypes.func,
  handleCloseFilter: PropTypes.func,
  deleteFunc: PropTypes.func,
  selected: PropTypes.array,
  handleRemoveSelected: PropTypes.func,
  generateCSVData: PropTypes.func,
  data: PropTypes.array,
  setFilterBy: PropTypes.func,
  setPage: PropTypes.func,
  filterValue: PropTypes.string,
  setFilterValue: PropTypes.func,
  departmentsData: PropTypes.array, // Added to propTypes
};

export default function TableToolbar({
  numSelected,
  filterName,
  setFilterName,
  filterBy,
  setFilterBy,
  setPage,
  onFilterName,
  model,
  handleOpenFilter,
  handleCloseFilter,
  deleteFunc,
  selected,
  handleRemoveSelected,
  generateCSVData,
  data,
  filterValue, // Hooked up to read active category filter state
  setFilterValue, // Hooked up to clear active category filter state
  departmentsData, // Fixed: Added to map numeric department IDs to their translated string names
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const theme = useTheme();
  const [dataInCSV, setDataInCSV] = useState('');
  const [isDownloadDebouncing, setIsDownloadDebouncing] = useState(false);

  // States to control the search reference dropdown popover
  const [searchMenuAnchor, setSearchMenuAnchor] = useState(null);

  const handleClearCategoryFilter = () => {
    if (setFilterValue) setFilterValue('');
    if (setPage) setPage(0);
  };

  const getFilterCategoryLabel = () => {
    let cleanFilter = (filterBy || '').toLowerCase().replace(/[\s_]+/g, '');

    // Normalization check: Strips any pre-existing "by" or "By" prefix from the category filter string
    if (cleanFilter.startsWith('by')) {
      cleanFilter = cleanFilter.slice(2);
      cleanFilter = cleanFilter.charAt(0).toLowerCase() + cleanFilter.slice(1);
    }

    // Maps strictly to your verified, active i18n translation keys
    if (cleanFilter === 'status') return t('rolesPage.status');
    if (cleanFilter === 'department' || cleanFilter === 'departmentname')
      return t('attributes.department');
    if (cleanFilter === 'priority') return t('attributes.priority');
    if (cleanFilter === 'tag') return t('attributes.tags');
    if (cleanFilter === 'role') return t('rolesPage.roleName');
    if (cleanFilter === 'createdby') return t('filters.byCreatedBy') || 'Created By';
    if (cleanFilter === 'language') return t('filters.byLanguage') || 'Language';
    if (cleanFilter === 'isanonymous') return t('filters.byIsAnonymous') || 'Anonymous';
    if (cleanFilter === 'ishidden') return t('filters.byIsHidden') || 'Type';
    if (cleanFilter === 'ismultiplesubmission')
      return t('filters.byMultipleSubmission') || 'Multiple Submission';
    return filterBy;
  };
  const getFilterValueLabel = () => {
    if (filterValue === null || filterValue === undefined || filterValue === '')
      return '';

    // Safely casts the value to a string to prevent .includes() from crashing on numeric IDs (e.g. department.id)
    const valueString = String(filterValue);
    const cleanVal = valueString.includes('|') ? valueString.split('|')[0] : valueString;

    // Checks local i18n translation folders
    const statusKey = `status.${cleanVal}`;
    const priorityKey = `priority.${cleanVal}`;
    const roleKey = `roles.${cleanVal}`;

    if (t(statusKey) !== statusKey && !t(statusKey).startsWith('status.'))
      return t(statusKey);
    if (t(priorityKey) !== priorityKey && !t(priorityKey).startsWith('priority.'))
      return t(priorityKey);
    if (t(roleKey) !== roleKey && !t(roleKey).startsWith('roles.')) return t(roleKey);

    // FIXED: Formats true/false strings for Status versus Yes/No boolean properties
    const isBooleanQuery = cleanVal === 'true' || cleanVal === 'false';
    const isStatusQuery =
      filterBy === 'isActive' || filterBy === 'status' || filterBy === 'isActiveForm';
    const isSurveyTypeQuery = filterBy === 'isHidden';

    if (isBooleanQuery) {
      if (isStatusQuery) {
        return cleanVal === 'true' ? t('userStatus.active') : t('userStatus.inactive');
      }
      if (isSurveyTypeQuery) {
        return cleanVal === 'true'
          ? t('formType.survey') || 'Survey'
          : t('formType.standard') || 'Standard Form';
      }
      return cleanVal === 'true'
        ? t('isMultipleSubmission.yes')
        : t('isMultipleSubmission.no');
    }

    // Resolves numeric department IDs to their actual names so the Chip displays "Department: Security Department" instead of "Department: 1"
    const cleanValNum = Number(cleanVal);
    if (departmentsData && departmentsData.length > 0 && !Number.isNaN(cleanValNum)) {
      const matchedDept = departmentsData.find((d) => Number(d.id) === cleanValNum);
      if (matchedDept) return matchedDept.name;
    }

    // Upper-case languages cleanly for visual style (e.g. 'en' -> 'EN')
    if (filterBy === 'language') {
      return String(cleanVal).toUpperCase();
    }

    return cleanVal;
  };

  const handleSearchMenuOpen = (event) => {
    setSearchMenuAnchor(event.currentTarget);
  };

  const handleSearchMenuClose = () => {
    setSearchMenuAnchor(null);
  };

  const handleSelectSearchField = (fieldKey) => {
    setFilterBy(fieldKey); // Updates active query reference (fullName, email, phoneNumber)
    setFilterName(''); // Clears search text
    if (setPage) setPage(0); // Resets page back to 0
    handleSearchMenuClose();
  };

  // Fixed: Converts the model checks to handle both static English and dynamic localized translation strings cleanly
  const isTicket = model === 'Ticket' || model === 'ticket';
  const isDepartmentModel =
    model === 'Department' || model === 'department' || model === t('nav.departments');
  const isFormModel = model === 'Form' || model === 'form' || model === t('nav.forms');

  // FIXED: Added safe check for Tag / Attribute model
  const isTagModel =
    model === 'Tag' ||
    model === 'tag' ||
    model === t('nav.tags') ||
    String(model).toLowerCase().includes('tag') ||
    String(model).toLowerCase().includes('attribut');

  const isClientCredentialsModel =
    model === 'ClientCredentials' ||
    model === 'clientCredentials' ||
    model === t('nav.clientCredentials') ||
    String(model).toLowerCase().includes('cred');

  const isShareableFormModel =
    model === 'ShareableForms' ||
    model === 'ShareableForm' ||
    model === t('nav.ShareableForms') ||
    String(model).toLowerCase().includes('shareable');

  const getSearchTranslationKey = () => {
    let cleanFilter = filterBy || '';

    // Normalization check: Converts the prefix check to lowercase to handle both "by" and "By" cases cleanly
    if (cleanFilter.toLowerCase().startsWith('by')) {
      cleanFilter = cleanFilter.slice(2);
      cleanFilter = cleanFilter.charAt(0).toLowerCase() + cleanFilter.slice(1);
    }

    if (isTicket) {
      if (cleanFilter === 'id') return 'filters.byId';
      if (cleanFilter === 'title') return 'filters.byTitle';
      if (cleanFilter === 'category') return 'filters.byCategory';
      if (cleanFilter === 'customerName' || cleanFilter === 'createdBy')
        return 'filters.byCreatedBy';
      if (cleanFilter === 'agentName' || cleanFilter === 'assignedTo')
        return 'filters.byTicketManager';
      if (cleanFilter === 'formName' || cleanFilter === 'formId')
        return 'filters.byFormName';
      if (cleanFilter === 'status') return 'filters.byStatus';
      if (cleanFilter === 'priority') return 'filters.byPriority';
      if (cleanFilter === 'department') return 'filters.byDepartment';
      if (cleanFilter === 'tag') return 'filters.byTag';
      return `filters.by${cleanFilter.charAt(0).toUpperCase() + cleanFilter.slice(1)}`;
    }

    if (isDepartmentModel) {
      if (cleanFilter === 'id') return 'filters.byId';
      // Fixed: Maps both 'name', 'department', and 'departmentName' targets safely to your correct 'filters.byDepartment' key
      if (
        cleanFilter === 'name' ||
        cleanFilter === 'department' ||
        cleanFilter === 'departmentName'
      )
        return 'filters.byDepartment';
      return `filters.${cleanFilter}`;
    }

    if (isFormModel) {
      if (cleanFilter === 'id') return 'filters.byId';
      if (cleanFilter === 'title' || cleanFilter === 'name') return 'filters.byName';
      if (cleanFilter === 'description') return 'attributes.description';
      // Fixed: Maps department selections on the Forms page cleanly to your correct "byDepartment" translation key
      if (
        cleanFilter === 'department' ||
        cleanFilter === 'departmentId' ||
        cleanFilter === 'departmentName'
      ) {
        return 'filters.byDepartment';
      }
      return `filters.${cleanFilter}`;
    }

    // FIXED: Maps your Tag/Attribute search targets (id, name, description)
    if (isTagModel) {
      if (cleanFilter === 'id') return 'filters.byId';
      if (cleanFilter === 'name') return 'filters.byName';
      if (cleanFilter === 'description') return 'attributes.description';
      if (cleanFilter === 'department') return 'filters.byDepartment';
      if (cleanFilter === 'createdBy' || cleanFilter === 'createdby')
        return 'filters.byCreatedBy';
      return `filters.${cleanFilter}`;
    }
    // FIXED: Maps your Client Credentials search targets (id, clientName, clientDomain)
    if (isClientCredentialsModel) {
      if (cleanFilter === 'id') return 'filters.byId';
      if (cleanFilter === 'clientname' || cleanFilter === 'clientName')
        return 'pages.clientCredentialsPage.clientName';
      if (cleanFilter === 'clientdomain' || cleanFilter === 'clientDomain')
        return 'pages.clientCredentialsPage.clientDomain';

      if (
        cleanFilter === 'enabled' ||
        cleanFilter === 'status' ||
        cleanFilter === 'isactive' ||
        cleanFilter === 'isActive'
      ) {
        return 'filters.byStatus';
      }

      return `filters.${cleanFilter}`;
    }
    // FIXED: Maps your Shareable Form / Survey search targets (id, form, generatedBy)
    if (isShareableFormModel) {
      if (cleanFilter === 'id') return 'filters.byId';
      if (
        cleanFilter === 'form' ||
        cleanFilter === 'form.title' ||
        cleanFilter === 'formtitle'
      )
        return 'filters.byFormName';
      if (
        cleanFilter === 'generatedby' ||
        cleanFilter === 'generatedby.username' ||
        cleanFilter === 'generatedbyusername'
      )
        return 'filters.byCreatedBy';

      // FIXED: Added category overrides inside the model block to prevent the early return from bypassing translations
      if (cleanFilter === 'language') return 'filters.byLanguage';
      if (cleanFilter === 'isanonymous' || cleanFilter === 'isAnonymous')
        return 'filters.byIsAnonymous';
      if (cleanFilter === 'ishidden' || cleanFilter === 'isHidden')
        return 'filters.byIsHidden';
      if (
        cleanFilter === 'ismultiplesubmission' ||
        cleanFilter === 'isMultipleSubmission'
      )
        return 'filters.byMultipleSubmission';
      if (cleanFilter === 'isactive' || cleanFilter === 'isActive')
        return 'filters.byStatus';

      return `filters.${cleanFilter}`;
    }

    // Standard User Model Fallbacks
    if (cleanFilter === 'id') return 'filters.byId';
    if (cleanFilter === 'fullName' || cleanFilter === 'name') return 'filters.byName';
    if (cleanFilter === 'email') return 'filters.byEmail';
    if (cleanFilter === 'status') return 'filters.byStatus';
    if (cleanFilter === 'role') return 'filters.byRole';
    // Global fallback maps all department-specific keys (departmentName, department, departmentId) to your correct translation key
    if (
      cleanFilter === 'departmentName' ||
      cleanFilter === 'department' ||
      cleanFilter === 'departmentId'
    )
      return 'filters.byDepartment';
    return `filters.${cleanFilter}`;
  };
  const getSearchPlaceholder = () => {
    const base = t('filters.search') || 'Search';
    const localizedField = t(getSearchTranslationKey()) || '';
    return `${base} ${localizedField.toLowerCase()}...`; // Combines "Search" and the localized reference
  };
  const { showConfirmModal } = useConfirmModal(
    t('pages.deletePage.confirmDelete'),
    `${t('pages.deletePage.areYouSure')}`,
    async () => handleDelete()
  );

  const handleDelete = () => {
    deleteFunc(selected);
  };

  // Disables input locking so typing is always responsive on text-searches
  const isDisabled = false;

  const handleClearSearch = () => {
    setFilterName('');
    onFilterName({ target: { value: '' } });
  };

  const handleDownloadCSV = () => {
    setIsDownloadDebouncing(true);
    const dataRes = data.filter((ticket) => selected.includes(ticket.id));
    if (model === 'Ticket') {
      const res = generateCSVData(dataRes);
      setDataInCSV(res);
    } else {
      const csvData = Papa.unparse(dataRes);
      setDataInCSV(csvData);
    }

    setTimeout(() => {
      setIsDownloadDebouncing(false);
    }, 1500);
  };

  return (
    <Toolbar sx={toolbarStyle(theme, numSelected)}>
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1}>
        {numSelected > 0 ? (
          <Box sx={isSmallScreen ? boxSelectedStyle : {}}>
            <Typography
              component="div"
              variant="subtitle1"
              sx={{
                fontSize: isSmallScreen ? '0.8rem' : '1rem',
              }}
            >
              {numSelected} {t('filters.selected')}
            </Typography>
            <Link
              sx={{
                cursor: 'pointer',
                fontSize: isSmallScreen && '0.6rem',
              }}
              variant="caption"
              onClick={handleRemoveSelected}
            >
              {t('buttons.removeSelected')}
            </Link>
          </Box>
        ) : (
          <StyledSearch
            name="searchBar"
            id="search-bar"
            value={filterName}
            onChange={onFilterName}
            disabled={isDisabled}
            placeholder={getSearchPlaceholder()} // Binds the dynamic placeholder text
            startAdornment={
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{
                    color: 'text.disabled',
                    width: isSmallScreen ? 16 : 20,
                    height: isSmallScreen ? 16 : 20,
                  }}
                />
              </InputAdornment>
            }
            endAdornment={
              filterName && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch}>
                    <Iconify
                      icon="eva:close-fill"
                      sx={{
                        color: 'text.disabled',
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              )
            }
            sx={{
              width: filterName ? '320px' : '240px',
              fontSize: isSmallScreen ? '0.75rem' : '1rem',
              '&.Mui-focused': {
                width: isSmallScreen ? 290 : 320,
              },
            }}
            size={isSmallScreen ? 'small' : 'medium'}
          />
        )}
      </Stack>
      {numSelected > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {data && (
            <Tooltip title={t('toolbar.downloadCSV')}>
              <IconButton
                onClick={handleDownloadCSV}
                href={`data:text/csv;charset=utf-8,${dataInCSV}`}
                download={
                  model === 'Ticket' ? t('toolbar.ticketsCSV') : t('toolbar.usersCSV')
                }
                disabled={isDownloadDebouncing}
              >
                <Iconify
                  icon={
                    isDownloadDebouncing
                      ? 'line-md:downloading-loop'
                      : 'material-symbols:download'
                  }
                  sx={{
                    width: isSmallScreen ? 16 : 20,
                    height: isSmallScreen ? 16 : 20,
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          <PermissionGuard permission="USER:DELETE">
            <Tooltip title={t('toolbar.delete')}>
              <IconButton onClick={showConfirmModal} sx={{ p: isSmallScreen ? 0.5 : 1 }}>
                <Iconify
                  icon="eva:trash-2-fill"
                  sx={{
                    width: isSmallScreen ? 16 : 20,
                    height: isSmallScreen ? 16 : 20,
                  }}
                />
              </IconButton>
            </Tooltip>
          </PermissionGuard>
        </Box>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          mt={isSmallScreen ? 0.5 : 1.5}
        >
          {/* Active Filter Removable Chip (only rendered when an active category filter is applied) */}
          {filterBy && filterValue && (
            <Chip
              label={`${getFilterCategoryLabel()}: ${getFilterValueLabel()}`}
              onDelete={handleClearCategoryFilter}
              size="small"
              sx={{
                borderRadius: '8px',
                fontWeight: 800,
                fontSize: '0.75rem',
                height: '28px',
                // Custom, high-contrast dynamic palette selection for dark mode
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.16 : 0.08
                  ),
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                '& .MuiChip-deleteIcon': {
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'theme.palette.primary.light'
                      : 'primary.main',
                  '&:hover': { color: 'primary.dark' },
                },
              }}
            />
          )}

          {/* Search Reference Dropdown Label (always visible since filterBy is mapped to searchBy) */}
          {true && ( // Always true to ensure search label and filter icon are visible on mount
            <>
              <Typography
                variant={isSmallScreen ? 'body2' : 'body1'}
                onClick={handleSearchMenuOpen} // Enables selection click
                sx={{
                  fontSize: isSmallScreen ? '0.7rem' : '1rem',
                  cursor: 'pointer', // Indicates clickability
                  textDecoration: 'underline',
                  textDecorationStyle: 'dashed',
                  textUnderlineOffset: '4px',
                  color: 'text.primary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {t('filters.search')} {t(getSearchTranslationKey())}
              </Typography>

              {/* Clickable dropdown Popover for selecting text-search targets */}
              <Popover
                open={Boolean(searchMenuAnchor)}
                anchorEl={searchMenuAnchor}
                onClose={handleSearchMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 1.5, width: 290 } }} // Increased width to prevent Italian string wrapping and icon clipping
              >
                {isTicket ? (
                  <>
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('title')}>
                      <Iconify
                        icon="ic:twotone-title"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byTitle')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('category')}>
                      <Iconify
                        icon="solar:folder-open-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byCategory')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('customerName')}>
                      <Iconify
                        icon="solar:user-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byCreatedBy')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('agentName')}>
                      <Iconify
                        icon="solar:user-speak-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byTicketManager')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('formName')}>
                      <Iconify
                        icon="solar:document-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byFormName')}
                    </MenuItem>
                  </>
                ) : isDepartmentModel ? (
                  <>
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('name')}>
                      <Iconify
                        icon="fluent-emoji-high-contrast:department-store"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byDepartment')}
                    </MenuItem>
                  </>
                ) : isFormModel ? (
                  <>
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('title')}>
                      <Iconify
                        icon="eva:person-fill"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />{' '}
                      {/* Fixed: Swapped to Name icon and translation */}
                      {t('filters.byName')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('description')}>
                      <Iconify
                        icon="solar:notes-bold-duotone"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('attributes.description')}
                    </MenuItem>
                  </>
                ) : isTagModel ? (
                  <>
                    {/* FIXED: Search fields specifically configured for your Tag/Attribute model */}
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('name')}>
                      <Iconify
                        icon="ic:twotone-title"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byName')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('description')}>
                      <Iconify
                        icon="solar:notes-bold-duotone"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('attributes.description')}
                    </MenuItem>
                    {/* FIXED: Added Created By as a text search field option */}
                    <MenuItem onClick={() => handleSelectSearchField('createdBy')}>
                      <Iconify
                        icon="solar:user-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byCreatedBy')}
                    </MenuItem>
                  </>
                ) : isClientCredentialsModel ? (
                  <>
                    {/* FIXED: Search fields specifically configured for your Client Credentials model */}
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('clientName')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('pages.clientCredentialsPage.clientName')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('clientDomain')}>
                      <Iconify
                        icon="solar:global-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('pages.clientCredentialsPage.clientDomain')}
                    </MenuItem>
                  </>
                ) : isShareableFormModel ? (
                  <>
                    {/* FIXED: Search fields specifically configured for your Shareable Form / Survey model */}
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('form')}>
                      <Iconify
                        icon="solar:document-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byFormName')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('generatedBy')}>
                      <Iconify
                        icon="solar:user-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byCreatedBy')}
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem onClick={() => handleSelectSearchField('id')}>
                      <Iconify
                        icon="solar:hashtag-bold"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byId')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('fullName')}>
                      <Iconify
                        icon="eva:person-fill"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byName')}
                    </MenuItem>
                    <MenuItem onClick={() => handleSelectSearchField('email')}>
                      <Iconify
                        icon="eva:email-fill"
                        sx={{ mr: 1.5, width: 18, height: 18 }}
                      />
                      {t('filters.byEmail')}
                    </MenuItem>
                  </>
                )}
              </Popover>
            </>
          )}

          {/* Dynamic category filter button (always visible unless it is the Departments layout) */}
          {!isDepartmentModel && (
            <ClickAwayListener
              onClickAway={() => {
                if (handleCloseFilter) handleCloseFilter();
              }}
            >
              <div>
                <Tooltip title="Filter list">
                  <IconButton
                    onClick={(e) => {
                      if (handleOpenFilter) handleOpenFilter(e);
                    }}
                    sx={{
                      p: isSmallScreen ? 0 : 1,
                      marginLeft: isSmallScreen ? 0 : 1,
                    }}
                  >
                    <Iconify
                      icon="ic:round-filter-list"
                      sx={{
                        width: isSmallScreen ? 18 : 20,
                        height: isSmallScreen ? 18 : 20,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </ClickAwayListener>
          )}
        </Stack>
      )}
    </Toolbar>
  );
}
